import { useState, useEffect, useCallback } from 'react';
import UpdateVariantForm from "../../components/update/update-variant-form";
import { useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthProvider";
import { useLanguage } from "../../context/LanguageProvider";
import { BASE_URL } from '../../apiConfig';
import { toast } from 'sonner';
import Loading from '../../cors/Loading';

export default function Page() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const { id } = useParams();
  const [variant, setVariant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchVariant = useCallback(async () => {
    if (!id) {
      setError(t('variants.id_missing'));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      if (!token) {
        toast.error(t('variants.auth_required'));
        setError(t('errors.auth_token_missing'));
        setLoading(false);
        return;
      }

      const res = await fetch(`${BASE_URL}/api/variants/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const errorData = await res.json();
        const errorMessage = errorData.message || `${t('variants.fetch_failed')}: ${res.statusText}`;
        throw new Error(errorMessage);
      }

      const data = await res.json();
      if (data.status && data.data) {
        setVariant(data.data);
      } else {
        throw new Error(data.message || t('variants.unexpected_format'));
      }
    } catch (err) {
      console.error("Error fetching variant:", err);
      setError(err.message || t('variants.error_loading'));
      toast.error(`${t('variants.error_prefix')} ${err.message || t('variants.fetch_failed')}`);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchVariant();
  }, [fetchVariant]);

  if (loading) {
    return (
      
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-red-200">
          <h2 className="text-xl font-bold text-red-700 mb-4">{t('variants.error_loading_title')}</h2>
          <p className="text-gray-700 mb-6">{error}</p>
          <button
            onClick={fetchVariant}
            className="px-6 py-3 bg-[var(--color-primary-500)] text-white font-semibold rounded-lg hover:bg-[var(--color-primary-400)] transition-colors"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (!variant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-gray-200">
          <h2 className="text-xl font-bold text-gray-700 mb-4">{t('variants.not_found')}</h2>
          <p className="text-gray-600">{t('variants.not_found_desc').replace('{id}', id)}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl max-xl:text-3xl  font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
            {t('variants.update_title')}
          </h1>
          <p className="text-sm max-xl:text-sm text-gray-600 max-w-2xl mx-auto">
            {t('variants.update_subtitle')}
          </p>
        </div>

        {/* Form Container */}
        <div className="max-w-2xl mx-auto">
          <UpdateVariantForm variant={variant} />
        </div>
      </div>
    </div>
  );
}